import React, { useState, useMemo, useCallback } from 'react';
import { gql } from '@apollo/client';

import useCustomer from 'hooks/useCustomer';
import { useQuery, useMutation } from 'hooks/apollo';
import { useMediaQuery } from 'react-responsive';

import { Module } from 'shared/services/prisma/types';
import theme from 'shared/constants/theme.json';
import { EDIT_SURVEY } from 'shared/constants/permissions';
import { MODULE_PATH } from 'shared/constants/survey';

import { Switch, Route, Redirect, Link, useRouteMatch, generatePath, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Layout, Button, Alert, Space, Divider } from 'antd';
import { EditOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';

import Page from 'components/Page';
import ErrorBoundary from 'components/ErrorBoundary';
import Loading from 'components/Loading';
import SurveySelect from 'components/SurveySelect';
import LicenseTag from 'components/LicenseTag';
import Content from 'components/Layout/Content';

import useDataSource from 'hooks/useDataSource';
import useUser from 'hooks/useUser';

import Filter from '../Filter';
import ResultsPage from '../ResultsPage';

import messages from '../messages';

import './survey-results.less';
import { toFilterSegmentInput } from '../mapper';

const GET_FILTER = gql`
  query Results_getFilter($displayedSurveyId: ID!, $surveyGroupId: ID!, $dataSourceId: ID!) {
    filter(surveyGroupId: $surveyGroupId, displayedSurveyId: $displayedSurveyId) {
      id
      surveyGroupId
      displayedSurveyId
      dateRange {
        id
        period
        periodType
        lastNumberInput
        comparePeriod
        compareToPrevious
        compareToPreviousValue
        specificDates
        compareSpecificDates
      }
      filterSegments {
        id
        isEnabled
        friendlyTitle
        responseCounts(dataSourceId: $dataSourceId)
        filterOptions { id relationId options type isEditable }
        syncToMatomo
        syncToWebsite { id }
        matomoCustomDimension
        shared
        createdByEmail
        sortIndex
        filterOptionsOperator
        filterOptionsInverted
        segment { id title query }
      }
      benchmarkSegments(includeAllCategories: true) {
        id
        isEnabled
        isConnected
        isGlobal
        color
        sortIndex
        surveyCategory {
          id
          title
        }
      }
    }
  }
`;

const GET_AVAILABLE_TABS = gql`
  query Results_availableResultTabs($surveyGroupId: ID!, $displayedSurveyId: ID!, $type: SurveyType) {
    availableResultTabs(surveyGroupId: $surveyGroupId, displayedSurveyId: $displayedSurveyId, type: $type)
  }
`;

const UPDATE_FILTER = gql`
  mutation Filter_updateFilter($filterId: ID!, $input: JSON!) {
    updateFilter(filterId: $filterId, input: $input) {
      id
    }
  }
`;

const REMOVE_SEGMENT = gql`
  mutation Filter_removeSegment($filterId: ID!, $segmentId: ID!) {
    removeFilterSegment(filterId: $filterId, segmentId: $segmentId) {
      id
    }
  }
`;

const DUPLICATE_SEGMENT = gql`
  mutation Filter_duplicateSegment($filterId: ID!, $segmentId: ID!) {
    duplicateFilterSegment(filterId: $filterId, segmentId: $segmentId) {
      id
    }
  }
`;

const { Sider } = Layout;

const extraFields = `
  surveyGroups { 
    id 
    title 
    surveys {
      id
      type
      title
      version
      primary
      language
      languageName
    }
  }
`;

const SurveyResults = () => {
  const history = useHistory();
  const { formatNumber } = useIntl();
  const { path, params } = useRouteMatch('/data-sources/:dataSourceId/:module/:page/:surveyGroupId/:displayedSurveyId?/:tab?');
  const { surveyGroupId, displayedSurveyId } = params;
  const { isSuperUser } = useUser();
  const { dataSource, dataSourceId, getDataSourceLicenseSetting, hasDataSourcePermission } = useDataSource({ extraFields });
  const { customer, loading: customerLoading } = useCustomer({ extraFields: 'allowPersonalData' });
  const surveyGroup = dataSource?.surveyGroups.find(sg => sg.id === surveyGroupId);
  const displayedSurvey = surveyGroup?.surveys.find(s => s.id === displayedSurveyId);
  const type = displayedSurvey?.type;
  const responseLimitSetting = params.module === MODULE_PATH[Module.CxSurveys] ? 'yearlyResponseLimit' : 'responseLimit';
  const responseLimit = getDataSourceLicenseSetting(responseLimitSetting);
  const noLicense = !dataSource?.activeLicense && !isSuperUser;

  const filterQuery = useQuery(GET_FILTER, {
    errorPolicy: 'all',
    variables: { surveyGroupId, displayedSurveyId, dataSourceId },
    skip: !surveyGroupId || !displayedSurveyId || noLicense,
  });
  const [newSegment, setNewSegment] = useState(false);

  const filter = filterQuery.data?.filter;
  const { id: filterId, filterSegments = [], benchmarkSegments = [], dateRange } = filter || {};

  const tabsQuery = useQuery(GET_AVAILABLE_TABS, {
    fetchPolicy: 'cache-first',
    variables: { surveyGroupId, displayedSurveyId, type },
    skip: !surveyGroupId || !displayedSurveyId,
  });

  const [updateFilter] = useMutation(UPDATE_FILTER, { refetchQueries: ['Results_getFilter'] });
  const handleFilterChange = input => updateFilter({ variables: { filterId, input } });

  const [deleteSegment] = useMutation(REMOVE_SEGMENT, { refetchQueries: ['Results_getFilter'] });
  const handleRemoveSegment = segmentId => deleteSegment({ variables: { filterId, segmentId } });

  const [duplicateSegment] = useMutation(DUPLICATE_SEGMENT, { refetchQueries: ['Results_getFilter'] });
  const handleDuplicateSegment = segmentId => duplicateSegment({ variables: { filterId, segmentId } });

  const isSmallScreen = useMediaQuery({ query: `(max-width: ${theme.laptopBreakpoint})` });
  const [siderCollapsed, collapseSider] = useState(isSmallScreen);
  const toggleSider = () => collapseSider(!siderCollapsed);

  const onDisplayedSurveyChange = useCallback((id) => {
    localStorage.setItem(`displayedSurveyId-${surveyGroupId}`, id);
    history.push(generatePath(path, { ...params, displayedSurveyId: id }));
  }, [history, path, params, surveyGroupId]);

  const onSelectChartSegment = useCallback(({ data, question = {} }) => {
    const { filterType, filterRelationId } = question;
    const optionIds = data.optionIds || [data.id];

    if (filterType && filterRelationId && optionIds?.length) {
      const filterOptions = [];
      let friendlyTitle = '';
      let filterOption;
      let isMergedSegment = false;

      if (data.segmentId) {
        const segmentInput = toFilterSegmentInput(filterSegments.find(s => s.id === data.segmentId));

        if (segmentInput.filterOptions.length) {
          filterOption = segmentInput.filterOptions.find(o => o.type === filterType && o.relationId === filterRelationId);
          filterOptions.push(...segmentInput.filterOptions);
          // Only merge if there is other filters than the one clicked
          if (segmentInput.filterOptions.length > 1 || !filterOption || filterOption.options.some(o => !optionIds.includes(o))) {
            friendlyTitle = `${segmentInput.friendlyTitle} + `;
            isMergedSegment = true;
          }
        }
      }

      if (filterOption) {
        filterOption.options.push(...optionIds.filter(o => !filterOption.options.includes(o)));
      } else {
        filterOptions.push({
          type: filterType,
          relationId: filterRelationId,
          options: optionIds,
        });
      }

      friendlyTitle += data.name;

      collapseSider(false);
      setNewSegment({
        isMergedSegment,
        friendlyTitle,
        filterOptions,
      });
    }
  }, [filterSegments, setNewSegment]);

  const tabsWithContent = tabsQuery.data?.availableResultTabs?.filter(t => t.content.length) || [];

  const routes = tabsWithContent.map(({ name, content }) => ({
    path: name,
    label: messages[name] ? <FormattedMessage {...messages[name]} /> : name,
    content,
  }));

  const enabledSegments = useMemo(() => filterSegments.filter(s => s.isEnabled), [filterSegments]);
  const enableBenchmarkSegments = useMemo(() => benchmarkSegments.filter(s => s.isEnabled), [benchmarkSegments]);

  const hasBenchmarkContent = tabsWithContent.some(r => r.hasBenchmarkContent);
  const canEdit = hasDataSourcePermission(EDIT_SURVEY);

  if (customerLoading || filterQuery.loading || tabsQuery.loading) {
    return <Loading />;
  }

  return (
    <Content>
      <Layout>
        <Page className="results">
          {filter && (
          <Switch>
            {routes.map((route) => (
              <Route key={route.path} path={generatePath(path, { ...params, tab: route.path })}>
                <ErrorBoundary>
                  <Page.Header
                    title={(
                      <Space split={<Divider type="vertical" />}>
                        <span>Results</span>
                        <SurveySelect surveyGroupId={surveyGroupId} value={displayedSurveyId} onChange={(surveyId) => onDisplayedSurveyChange(surveyId)} />
                        <span>{route.label}</span>
                      </Space>
                    )}
                    extra={(
                      canEdit && <Link to={generatePath(path, { ...params, page: 'edit', tab: null })}><Button icon={<EditOutlined />} type="primary">Edit survey</Button></Link>
                    )}
                  >
                    {responseLimit > 0 && <Alert message={<FormattedMessage {...messages.responseLimit} values={{ value: formatNumber(responseLimit), license: <LicenseTag setting={responseLimitSetting} showTooltip /> }} />} type="warning" showIcon style={{ margin: '1em 0' }} />}
                    {noLicense && <Alert message={<FormattedMessage {...messages.noLicense} />} type="warning" showIcon style={{ margin: '1em 0' }} />}
                  </Page.Header>
                  <ResultsPage
                    surveyGroupId={surveyGroupId}
                    displayedSurveyId={displayedSurveyId}
                    dateRange={dateRange}
                    filterSegments={enabledSegments}
                    benchmarkSegments={enableBenchmarkSegments}
                    content={route.content}
                    allowPersonalData={customer.allowPersonalData}
                    onSelectChartSegment={onSelectChartSegment}
                  />
                </ErrorBoundary>
              </Route>
            ))}
            {routes.length && <Route path="*"><Redirect to={generatePath(path, { ...params, tab: routes[0].path })} /></Route>}
          </Switch>
          )}
        </Page>
        {filter && (
        <Sider width={300} className="filter-sider" theme="light" collapsible trigger={null} collapsed={siderCollapsed} collapsedWidth={42}>
          <div className="filter-sider__header">
            <FormattedMessage {...messages.filter} />
            <Button
              onClick={toggleSider}
              icon={siderCollapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              type="link"
            />
          </div>
          {!siderCollapsed && (
          <Filter
            customerId={customer.id}
            dataSourceId={dataSourceId}
            filter={filter}
            onChange={handleFilterChange}
            onRemoveSegment={handleRemoveSegment}
            onDuplicateSegment={handleDuplicateSegment}
            enabledCount={enabledSegments.length}
            hasBenchmarkContent={hasBenchmarkContent}
            newSegment={newSegment}
            setNewSegment={setNewSegment}
          />
          )}
        </Sider>
        )}
      </Layout>
    </Content>
  );
};

export default SurveyResults;
